import type {
  ComposerProps,
  IMessage,
  InputToolbarProps,
} from 'react-native-gifted-chat';

import type {
  NativeSyntheticEvent,
  TextInputChangeEventData,
} from 'react-native';
import { Platform, StyleSheet } from 'react-native';

import { getColorTokenValue } from '@utility-nyc/react-native-ui-config';
import { InputToolbar } from 'react-native-gifted-chat';
import { useMedia } from 'tamagui';

import { getTestProps } from '@south-street-app/atoms';
import { getSizeTokenValue, radius } from '@south-street-app/theme';

const styles = StyleSheet.create({
  inputPrimaryStyle: {
    backgroundColor: '#FFFFFF',
    borderColor: getColorTokenValue('$mono300'),
    borderWidth: 1,
    borderRadius: 18,
    minHeight: getSizeTokenValue('$14'),
    alignItems: 'flex-end',
    paddingHorizontal: getSizeTokenValue('$3'),
    paddingVertical: getSizeTokenValue('$2'),
    width: '100%',
  },
  inputPrimaryStyleDesktop: {
    backgroundColor: '#FFFFFF',
    borderWidth: 0,
    borderRadius: radius.true,
    minHeight: getSizeTokenValue('$14'),
    alignItems: 'flex-end',
    paddingHorizontal: getSizeTokenValue('$3'),
    paddingVertical: getSizeTokenValue('$2'),
    width: '100%',
  },
  inputContainerStyle: {
    backgroundColor: 'transparent',
    padding: 0,
    borderTopWidth: 0,
    width: '100%',
  },
  inputContainerStyleDesktop: {
    backgroundColor: 'transparent',
    paddingBottom: getSizeTokenValue('$5'),
    paddingHorizontal: getSizeTokenValue('$5'),
    paddingTop: 0,
    borderTopWidth: 0,
    justifyContent: 'flex-end',
    width: '100%',
  },
});

type ChatInputProps = InputToolbarProps<IMessage> &
  Pick<ComposerProps, 'onInputSizeChanged' | 'textInputProps' | 'textInputStyle'>;

type WebTextInputTarget = EventTarget & {
  scrollHeight?: number;
  scrollWidth?: number;
  style?: {
    height: string;
  };
};

const getWebTextInputContentSize = (
  target: EventTarget | null,
): { height: number; width: number } | null => {
  if (Platform.OS !== 'web' || !target) {
    return null;
  }

  const textInputTarget = target as WebTextInputTarget;

  if (typeof textInputTarget.scrollHeight !== 'number') {
    return null;
  }

  const previousHeight = textInputTarget.style?.height;

  if (textInputTarget.style) {
    textInputTarget.style.height = '0px';
  }

  const contentSize = {
    height: textInputTarget.scrollHeight,
    width: textInputTarget.scrollWidth ?? 0,
  };

  if (textInputTarget.style && typeof previousHeight === 'string') {
    textInputTarget.style.height = previousHeight;
  }

  return contentSize;
};

const ChatInput = (props: ChatInputProps) => {
  const { desktop } = useMedia();
  const textInputProps = {
    accessibilityLabel: 'Message',
    ...getTestProps('Message'),
    ...props.textInputProps,
    onChange: (
      event: NativeSyntheticEvent<TextInputChangeEventData> & {
        target: EventTarget | null;
      },
    ) => {
      props.textInputProps?.onChange?.(event);

      const contentSize = getWebTextInputContentSize(event.target);

      if (contentSize) {
        props.onInputSizeChanged?.(contentSize);
      }
    },
  };
  const inputToolbarProps = {
    ...props,
    textInputProps,
  } as InputToolbarProps<IMessage>;

  return (
    <InputToolbar
      {...inputToolbarProps}
      primaryStyle={
        desktop ? styles.inputPrimaryStyleDesktop : styles.inputPrimaryStyle
      }
      containerStyle={
        desktop ? styles.inputContainerStyleDesktop : styles.inputContainerStyle
      }
    />
  );
};

export { ChatInput };
